import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import axios from 'axios';
import LeadSearchInput from '../leads/LeadSearchInput';

// Event types with corresponding colors (for reference)
const eventTypeColors = {
  course: '#10B981',  // green (primary event type)
  demo: '#3B82F6',    // blue
  'sales call': '#F59E0B'  // amber/orange for sales calls
  // Note: Course events without tutors are shown in red (#EF4444) 
  // Red is not used for tutor colors to avoid confusion
};

// Recurrence frequency options
const recurrenceFrequencies = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 Weeks' },
  { value: 'monthly', label: 'Monthly' }
];

const EventFormModal = ({
  isOpen,
  onClose,
  currentEvent,
  setCurrentEvent,
  isEditing,
  selectedEventId,
  handleSaveEvent,
  leads,
  deals,
  tutors,
  courses
}) => {
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen]);

  // Handle form close and cleanup
  const handleClose = () => {
    onClose();
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle checkbox inputs
    if (type === 'checkbox') {
      setCurrentEvent(prev => ({
        ...prev,
        [name]: checked
      }));
    } 
    // Handle number inputs
    else if (type === 'number') {
      // Allow empty string to clear the input before typing a new number
      if (value === '') {
        setCurrentEvent(prev => ({
          ...prev,
          [name]: value  // Store empty string temporarily while typing
        }));
      } else {
        setCurrentEvent(prev => ({
          ...prev,
          [name]: parseInt(value, 10) || 1
        }));
      }
    } 
    // Handle event type change
    else if (name === 'event_type') {
      if (value === 'course') {
        // For course events, we need to clear related lead and deal
        setCurrentEvent(prev => ({
          ...prev,
          [name]: value,
          related_lead_id: null,
          related_deal_id: null
        }));
      } else if (value === 'demo' || value === 'sales call') {
        // For demo and sales call events, handle specially:
        // 1. Clear course-specific fields
        // 2. Clear recurring event settings (not allowed for sales calls and demos)
        // 3. Also clear related_deal_id (only related_lead_id is used)
        setCurrentEvent(prev => ({
          ...prev,
          [name]: value,
          course_id: null,
          tutor_id: null,
          is_recurring: false,
          recurrence_count: 1,
          related_deal_id: null
        }));
      } else {
        // For any other event types
        setCurrentEvent(prev => ({
          ...prev,
          [name]: value
        }));
      }
    }
    // Handle course_id specially to ensure it's stored as a number
    // and to set the default max_students from the selected course
    else if (name === 'course_id') {
      const courseId = value ? parseInt(value, 10) : null;
      
      setCurrentEvent(prev => {
        // If a course is selected, find its max_students
        if (courseId) {
          const selectedCourse = courses.find(c => c.id === courseId);
          if (selectedCourse) {
            // Check if this is a course change (different from previous selection)
            const isNewCourseSelection = prev.course_id !== courseId;

            return {
              ...prev,
              [name]: courseId,
              // When changing courses, ALWAYS update max_students to match the new course
              // Only preserve manual values when the course hasn't changed
              max_students: isNewCourseSelection ? selectedCourse.max_students : (prev.max_students || selectedCourse.max_students)
            };
          }
        }
        
        return {
          ...prev,
          [name]: courseId
        };
      });
    }
    // Handle tutor_id specially to ensure it's stored as a number
    else if (name === 'tutor_id') {
      setCurrentEvent(prev => ({
        ...prev,
        [name]: value ? parseInt(value, 10) : null
      }));
    }
    // Handle all other inputs
    else {
      setCurrentEvent(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handle date and time changes
  const handleDateChange = (field, date) => {
    // Create a copy of current event data
    const updatedEvent = {
      ...currentEvent,
      [field]: date
    };
    
    // Check for invalid time combinations (end time before or equal to start time)
    if (field === 'end_time' && new Date(updatedEvent.end_time) <= new Date(updatedEvent.start_time)) {
      // If end time is being set to before start time, show a warning
      // We use a simple alert here because the notification component might not be available in this modal component
      alert('End time must be after start time');
      
      // Set a valid end time (start time + 30 minutes)
      const newEndTime = new Date(updatedEvent.start_time);
      newEndTime.setMinutes(newEndTime.getMinutes() + 30);
      updatedEvent.end_time = newEndTime;
    }
    
    // If start time is being changed to after end time, adjust end time
    if (field === 'start_time' && new Date(updatedEvent.start_time) >= new Date(updatedEvent.end_time)) {
      // Set end time to start time + 30 minutes
      const newEndTime = new Date(updatedEvent.start_time);
      newEndTime.setMinutes(newEndTime.getMinutes() + 30);
      updatedEvent.end_time = newEndTime;
    }
    
    // Update state with validated times
    setCurrentEvent(updatedEvent);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 pt-10 pb-10">
      <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-auto my-10 p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {isEditing ? 'Edit Event' : 'Add New Event'}
          </h3>
          <button 
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <form onSubmit={handleSaveEvent} className="space-y-4">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700">Title</label>
            <input
              type="text"
              name="title"
              id="title"
              required
              value={currentEvent.title}
              onChange={handleInputChange}
              className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="start_time" className="block text-sm font-medium text-gray-700">Start Time</label>
              <DatePicker
                selected={currentEvent.start_time}
                onChange={(date) => handleDateChange('start_time', date)}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={15}
                dateFormat="MMMM d, yyyy HH:mm"
                className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              />
            </div>
            
            <div>
              <label htmlFor="end_time" className="block text-sm font-medium text-gray-700">End Time</label>
              <DatePicker
                selected={currentEvent.end_time}
                onChange={(date) => handleDateChange('end_time', date)}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={15}
                dateFormat="MMMM d, yyyy HH:mm"
                className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="location" className="block text-sm font-medium text-gray-700">Location</label>
              <input
                type="text"
                name="location"
                id="location"
                value={currentEvent.location || ''}
                onChange={handleInputChange}
                className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                placeholder="Physical location or virtual meeting link"
              />
            </div>
            <div>
              <label htmlFor="country" className="block text-sm font-medium text-gray-700">Country</label>
              <select
                name="country"
                id="country"
                value={currentEvent.country || ''}
                onChange={handleInputChange}
                className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">Select Country</option>
                <option value="Norway">Norway</option>
                <option value="Sweden">Sweden</option>
                <option value="Germany">Germany</option>
                <option value="United States">United States</option>
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="event_type" className="block text-sm font-medium text-gray-700">Event Type</label>
              <select
                name="event_type"
                id="event_type"
                value={currentEvent.event_type || 'course'}
                onChange={handleInputChange}
                className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="course">Course</option>
                <option value="demo">Demo</option>
                <option value="sales call">Sales Call</option>
              </select>
            </div>

            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700">Status</label>
              <select
                name="status"
                id="status"
                value={currentEvent.status || 'Scheduled'}
                onChange={handleInputChange}
                className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="Scheduled">Scheduled</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
          </div>
          
          {/* Recurring Event Options - only show for course events */}
          {!isEditing && currentEvent.event_type === 'course' && (
            <div className="border border-gray-200 rounded-md p-4 space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="is_recurring"
                  id="is_recurring"
                  checked={currentEvent.is_recurring}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_recurring" className="ml-2 block text-sm font-medium text-gray-700">
                  Make this a recurring event
                </label>
              </div>
              
              {currentEvent.is_recurring && (
                <div className="pt-2 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="recurrence_frequency" className="block text-sm font-medium text-gray-700">Frequency</label>
                      <select
                        name="recurrence_frequency"
                        id="recurrence_frequency"
                        value={currentEvent.recurrence_frequency}
                        onChange={handleInputChange}
                        className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      >
                        {recurrenceFrequencies.map(freq => (
                          <option key={freq.value} value={freq.value}>{freq.label}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label htmlFor="recurrence_count" className="block text-sm font-medium text-gray-700">
                        Number of Occurrences
                      </label>
                      <input
                        type="number"
                        name="recurrence_count"
                        id="recurrence_count"
                        min="2"
                        max="52"
                        value={currentEvent.recurrence_count}
                        onChange={handleInputChange}
                        className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      />
                      <p className="mt-1 text-xs text-gray-500">Max: 52 occurrences</p>
                    </div>
                  </div>
                  
                  <div className="pt-2">
                    <p className="text-sm text-gray-600">
                      This will create {currentEvent.recurrence_count === '' ? '1' : currentEvent.recurrence_count} separate events
                      {currentEvent.recurrence_frequency === 'daily' && ' occurring every day.'}
                      {currentEvent.recurrence_frequency === 'weekly' && ' occurring every week.'}
                      {currentEvent.recurrence_frequency === 'biweekly' && ' occurring every 2 weeks.'}
                      {currentEvent.recurrence_frequency === 'monthly' && ' occurring every month.'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Related Lead/Deal fields - only show if event type is not "course" */}
          {currentEvent.event_type !== 'course' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="related_lead_id" className="block text-sm font-medium text-gray-700">Related Lead</label>
                <LeadSearchInput
                  value={currentEvent.related_lead_id || ''}
                  onChange={(leadId) => {
                    setCurrentEvent(prev => ({
                      ...prev,
                      related_lead_id: leadId
                    }));
                  }}
                  placeholder="Search for a lead..."
                />
              </div>
              
              {/* Only show related deal field for events that are neither sales calls nor demos */}
              {currentEvent.event_type !== 'sales call' && currentEvent.event_type !== 'demo' && (
                <div>
                  <label htmlFor="related_deal_id" className="block text-sm font-medium text-gray-700">Related Deal</label>
                  <select
                    name="related_deal_id"
                    id="related_deal_id"
                    value={currentEvent.related_deal_id || ''}
                    onChange={handleInputChange}
                    className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="">None</option>
                    {deals.map(deal => (
                      <option key={deal.id} value={deal.id}>{deal.title}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}
          
          {/* Course and Tutor selection for course events */}
          {currentEvent.event_type === 'course' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="course_id" className="block text-sm font-medium text-gray-700">
                    Course
                  </label>
                  <select
                    name="course_id"
                    id="course_id"
                    value={currentEvent.course_id || ''}
                    onChange={handleInputChange}
                    className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    required={currentEvent.event_type === 'course'}
                  >
                    <option value="">Select a course</option>
                    {courses.map(course => (
                      <option key={course.id} value={course.id}>
                        {course.name} ({course.age_group}, {course.level})
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label htmlFor="tutor_id" className="block text-sm font-medium text-gray-700">
                    Assigned Tutor
                  </label>
                  <select
                    name="tutor_id"
                    id="tutor_id"
                    value={currentEvent.tutor_id || ''}
                    onChange={handleInputChange}
                    className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    size={tutors.length > 10 ? 10 : tutors.length}
                  >
                    <option value="">Select a tutor</option>
                    {tutors.map(tutor => (
                      <option key={tutor.id} value={tutor.id}>
                        {tutor.name} {tutor.university ? `(${tutor.university})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              {/* Max Students field */}
              <div className="mt-4">
                <label htmlFor="max_students" className="block text-sm font-medium text-gray-700">
                  Maximum Students
                </label>
                <input
                  type="number"
                  name="max_students"
                  id="max_students"
                  min="1"
                  max="100"
                  value={currentEvent.max_students || ''}
                  onChange={handleInputChange}
                  className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Leave empty to use course default. Override for special circumstances like smaller classes.
                </p>
              </div>
            </>
          )}
          
          {/* Reminder settings for course events */}
          {currentEvent.event_type === 'course' && (
            <div className="border border-gray-200 rounded-md p-4 mt-4">
              <h4 className="font-medium text-gray-700 mb-2">Reminder Settings</h4>
              <p className="text-sm text-gray-500 mb-3">
                Configure automatic email reminders that will be sent 24 hours before the event.
              </p>
              <div className="space-y-3">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="send_tutor_reminder"
                    name="send_tutor_reminder"
                    checked={currentEvent.send_tutor_reminder || false}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="send_tutor_reminder" className="ml-2 block text-sm text-gray-700">
                    Send reminder to tutor
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="send_student_reminder"
                    name="send_student_reminder"
                    checked={currentEvent.send_student_reminder || false}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="send_student_reminder" className="ml-2 block text-sm text-gray-700">
                    Send reminder to students
                  </label>
                </div>
              </div>
            </div>
          )}
          
          {/* Public booking option for course events */}
          {currentEvent.event_type === 'course' && (
            <div className="border border-gray-200 rounded-md p-4 mt-4">
              <h4 className="font-medium text-gray-700 mb-2">Public Booking</h4>
              <div className="flex items-center mb-2">
                <input
                  type="checkbox"
                  id="is_public"
                  name="is_public"
                  checked={currentEvent.is_public || false}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_public" className="ml-2 block text-sm text-gray-700">
                  Allow public booking of this class
                </label>
              </div>
              <p className="text-sm text-gray-500">
                When enabled, this class will be visible on your public booking page and students can book it directly.
              </p>
              
              {currentEvent.is_public && (
                <>
                  {/* Payment settings for public bookings */}
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <h5 className="font-medium text-gray-700 mb-2">Payment Settings</h5>
                    <div className="flex items-center mb-3">
                      <input
                        type="checkbox"
                        id="payment_required"
                        name="payment_required"
                        checked={currentEvent.payment_required || false}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="payment_required" className="ml-2 block text-sm text-gray-700">
                        Require payment to book this class
                      </label>
                    </div>
                    
                    {currentEvent.payment_required && (
                      <div className="flex space-x-3 mb-2">
                        <div className="w-1/2">
                          <label htmlFor="price" className="block text-sm font-medium text-gray-700">Price</label>
                          <div className="mt-1 relative rounded-md shadow-sm">
                            <input
                              type="number"
                              name="price"
                              id="price"
                              value={currentEvent.price ? Math.floor(currentEvent.price / 100) : ''}
                              onChange={(e) => {
                                // Parse the input value as an integer
                                const inputValue = e.target.value;
                                // Handle empty input
                                if (inputValue === '') {
                                  setCurrentEvent({
                                    ...currentEvent,
                                    price: 0
                                  });
                                  return;
                                }
                                
                                // Only store valid integer values
                                if (/^\d+$/.test(inputValue)) {
                                  const priceInCents = parseInt(inputValue) * 100;
                                  setCurrentEvent({
                                    ...currentEvent,
                                    price: priceInCents
                                  });
                                }
                              }}
                              className="focus:ring-blue-500 focus:border-blue-500 block w-full pr-12 sm:text-sm border-gray-300 rounded-md"
                              placeholder="Enter price"
                              step="1"
                              min="0"
                            />
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                              <span className="text-gray-500 sm:text-sm">
                                {currentEvent.currency || 'USD'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="w-1/2">
                          <label htmlFor="currency" className="block text-sm font-medium text-gray-700">Currency</label>
                          <select
                            id="currency"
                            name="currency"
                            value={currentEvent.currency || 'USD'}
                            onChange={handleInputChange}
                            className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          >
                            <option value="USD">USD</option>
                            <option value="EUR">EUR</option>
                            <option value="GBP">GBP</option>
                            <option value="NOK">NOK</option>
                            <option value="SEK">SEK</option>
                            <option value="DKK">DKK</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-2 p-2 bg-blue-50 rounded text-sm text-blue-800">
                    <p className="mb-1">Public booking link:</p>
                    <code className="block bg-white p-2 rounded border border-blue-200 overflow-x-auto">
                      {window.location.origin}/booking.html?company={1 /* For localhost testing, hardcode to company ID 1 */}
                    </code>
                  </div>
                </>
              )}
            </div>
          )}
          
          {/* Student management info message */}
          {!isEditing && currentEvent.event_type === 'course' && (
            <div className="border border-gray-200 rounded-md p-4 mt-4 bg-blue-50">
              <h4 className="font-medium text-gray-700 mb-2">Student Management</h4>
              <p className="text-sm text-gray-600 mb-0">
                Students can be added to this course after creating the event using the student management section in the event details.
              </p>
            </div>
          )}
          
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description</label>
            <textarea
              name="description"
              id="description"
              rows="3"
              value={currentEvent.description || ''}
              onChange={handleInputChange}
              className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              placeholder="Event details, agenda, notes, etc."
            ></textarea>
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700"
            >
              {isEditing ? 'Update Event' : 'Save Event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

EventFormModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentEvent: PropTypes.object.isRequired,
  setCurrentEvent: PropTypes.func.isRequired,
  isEditing: PropTypes.bool.isRequired,
  selectedEventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleSaveEvent: PropTypes.func.isRequired,
  leads: PropTypes.array.isRequired,
  deals: PropTypes.array.isRequired,
  tutors: PropTypes.array.isRequired,
  courses: PropTypes.array.isRequired
};

export default EventFormModal;